import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { DashboardSessionKeyProvider, useDashboardSessionKey } from './DashboardSessionKeyContext';
import { OrgSwitcher } from './components/OrgSwitcher';
import { UserMenu } from './components/UserMenu';
import { Logo } from './components/Logo';
import { Alert } from '../components/ui/Alert';
import { LoadingState } from '../components/ui/Spinner';
import { listProviders } from '../lib/api-client';

interface NavItem {
  to: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Visão geral' },
  { to: '/quotes', label: 'Cotação' },
  { to: '/labels', label: 'Etiquetas' },
  { to: '/tracking', label: 'Rastreio' },
  { to: '/providers', label: 'Provedores' },
  { to: '/api-keys', label: 'API keys' },
  { to: '/routing', label: 'Roteamento' },
  { to: '/usage', label: 'Uso' },
  { to: '/costs', label: 'Custos' },
  // '/billing' (TTP-251) deliberadamente ausente desta release — feature
  // ainda não promovida pra main (credenciais reais de Mercado Pago
  // Assinaturas não confirmadas, ver docs/product/pricing.md em develop).
  // Reintroduzir aqui quando TTP-251 for liberada.
];

/**
 * Wraps the shell's content — every post-onboarding page needs the
 * dashboard session key (TTP-244's "Decisão arquitetural CRÍTICA", see
 * `DashboardSessionKeyContext.tsx`) before it can call any `/v1` route.
 * Mirrors `features/onboarding/guards.tsx`'s `EnsureApiKeysReady`, scoped to
 * this shell instead of the onboarding flow.
 */
function EnsureDashboardSessionKeyReady({ children }: { children: ReactNode }) {
  const { key, loading, error, retry } = useDashboardSessionKey();

  if (key) {
    return <>{children}</>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert variant="error" title="Não foi possível preparar a sessão do dashboard">
          {error}
        </Alert>
        <Button type="button" variant="outline" onClick={retry}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  return <LoadingState label={loading ? 'Preparando sua sessão…' : 'Carregando…'} />;
}

/**
 * Global "N provedores precisam de reconexão" banner (edge case obrigatório
 * do PRD: "credencial revogada no provedor → alertar + status no
 * dashboard") — complements the per-card alert already shown by
 * `ProviderConnectCard.tsx` on `/providers`, so uma credencial invalidada é
 * visível de qualquer lugar do shell, não só quando o usuário abre aquela
 * página.
 */
function ProviderReconnectBanner() {
  const { key } = useDashboardSessionKey();
  const [invalidCount, setInvalidCount] = useState(0);

  useEffect(() => {
    if (!key) {
      setInvalidCount(0);
      return;
    }
    let cancelled = false;
    listProviders(key.key)
      .then((connections) => {
        if (!cancelled) {
          setInvalidCount(connections.filter((c) => c.status === 'invalid_credentials').length);
        }
      })
      .catch(() => {
        // Best-effort banner only — a failed fetch just means no banner this load, not a blocking error.
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  if (invalidCount === 0) {
    return null;
  }

  return (
    <Alert
      variant="warning"
      title={`${invalidCount} provedor${invalidCount > 1 ? 'es' : ''} ${invalidCount > 1 ? 'precisam' : 'precisa'} de reconexão`}
    >
      A credencial foi invalidada no provedor. Acesse{' '}
      <NavLink to="/providers" className="font-medium text-primary underline-offset-4 hover:underline">
        Provedores
      </NavLink>{' '}
      para reconectar sua conta.
    </Alert>
  );
}

function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return `block whitespace-nowrap border-l-2 px-3 py-2 font-mono text-xs font-medium uppercase tracking-widest ${
    isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
  }`;
}

/**
 * Rótulo do header (`/ labels`, `/ providers`, ...) derivado do path atual —
 * cai no próprio `pathname` quando a rota não bate com nenhum item de
 * `NAV_ITEMS` (ex.: `/labels/:labelId`, `/orgs/new`), em vez de mostrar nada.
 */
function useHeaderPathLabel(): string {
  const { pathname } = useLocation();
  const match = NAV_ITEMS.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  return match ? `/ ${match.label.toLowerCase()}` : pathname;
}

function DashboardHeader() {
  const pathLabel = useHeaderPathLabel();
  return (
    <div className="flex h-[60px] shrink-0 items-center justify-between border-b border-border px-4 lg:px-8">
      <span className="font-mono text-xs tracking-wide text-muted-foreground">{pathLabel}</span>
      <UserMenu />
    </div>
  );
}

/**
 * Post-onboarding dashboard shell (TTP-244; redesenhado no piloto de design
 * brutalist). Wraps `/` (Visão geral, TTP-282), `/quotes` (TTP-287),
 * `/labels`, `/tracking`, `/providers`, `/api-keys`, `/routing`, `/usage`
 * (TTP-245), `/costs` (TTP-250) route with: o símbolo/wordmark (`Logo.tsx`), o org switcher (P3), um
 * header por página com o path atual + o avatar do usuário, a dashboard
 * session key (nunca as chaves reveladas uma única vez na Tela 5 do
 * onboarding), e o banner global de reconexão.
 */
export function AppShell() {
  return (
    <DashboardSessionKeyProvider>
      <div className="flex min-h-screen flex-col bg-background font-sans text-foreground lg:flex-row">
        <aside className="flex flex-col gap-6 border-b border-border px-4 py-4 lg:w-64 lg:shrink-0 lg:border-b-0 lg:border-r lg:px-6 lg:py-6">
          <div className="flex items-center justify-between gap-4 lg:flex-col lg:items-start lg:gap-4">
            <Logo />
            <div className="flex flex-col items-start gap-2">
              <OrgSwitcher />
              <NavLink
                to="/orgs/new"
                className="font-mono text-xs font-medium text-primary underline-offset-4 hover:underline"
              >
                + Adicionar empresa
              </NavLink>
            </div>
          </div>
          <nav aria-label="Navegação do dashboard" className="lg:flex-1">
            <ul className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.to === '/'} className={navLinkClassName}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="hidden flex-col gap-1 border-t border-border pt-4 font-mono text-[10.5px] text-muted-foreground lg:flex">
            <span>API v1 · us-central1</span>
            <span>triviofy by Tervior</span>
          </div>
        </aside>

        <div className="flex flex-1 flex-col">
          <DashboardHeader />
          <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8">
            <EnsureDashboardSessionKeyReady>
              <div className="flex flex-col gap-6">
                <ProviderReconnectBanner />
                <Outlet />
              </div>
            </EnsureDashboardSessionKeyReady>
          </main>
        </div>
      </div>
    </DashboardSessionKeyProvider>
  );
}
